<template>
  <div class="gap-2 flex items-center" :style="{ paddingLeft: `${indentLevel}px` }">
    <!-- Icon -->
    <div v-if="!hideIcon">
      <font-awesome-icon
        :icon="icon"
        :class="iconClass"
      />
    </div>
    <!-- Link -->
    <router-link v-if="routePath" :to="routePath">
      <span class="sensitive text-neutral-900 font-semibold hover:text-neutral-700 hover:underline">
        {{ name }}
      </span>
    </router-link>
    <span v-else class="sensitive text-gray-900">{{ name }}</span>
  </div>
</template>

<script>
import { canAccessEntity } from '@/utils/access';
import { getEntityIcon } from '@/utils/entityIcons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'CC_Router_Link_To_Entity',
  components: {
    FontAwesomeIcon
  },
  props: {
    params: {
      type: Object,
      required: true
    }
  },
  computed: {
    data() {
      return this.params.data || {};
    },

    // Allow configuration of which field contains the name
    nameField() {
      return this.params.nameField || 'name';
    },

    name() {
      // Use configured name field, fallback to 'name', then to value
      return this.data[this.nameField] || this.data.name || this.params.value || 'Unknown';
    },

    // Allow configuration of which field contains the ID
    idField() {
      return this.params.idField || 'id';
    },

    entityId() {
      return this.data[this.idField] || this.data.id || '';
    },

    cleanId() {
      const canAccess = canAccessEntity(this.entityId);
      if (!canAccess) {
        return null;
      }
      const id = String(this.entityId);
      return id.replace(/^entity_/, '');
    },

    routePath() {
      if (!this.cleanId) return null;

      // Allow custom route type to be specified
      if (this.params.routeType) {
        return `/${this.params.routeType}/${this.cleanId}`;
      }

      // Default routing logic
      return `/entity/${this.cleanId}`;
    },

    // Option to hide icon
    hideIcon() {
      return this.params.hideIcon || false;
    },

    icon() {
      // Check custom iconField first (for AG Grid column configs)
      const iconField = this.params.iconField || 'icon';
      if (iconField !== 'icon' && this.data[iconField]) {
        const raw = this.data[iconField];
        try {
          if (Array.isArray(raw)) return raw;
          if (typeof raw === 'string') return JSON.parse(raw);
        } catch (e) { /* fall through */ }
      }

      return getEntityIcon(this.data, this.params.defaultIcon || ['fas', 'user-doctor']);
    },

    iconClass() {
      // Allow custom icon class or use default
      return this.params.iconClass || 'text-lg';
    },

    indentLevel() {
      if (!this.params.enableIndentation) return 0;
      const level = this.params.node?.level || 0;
      return level * (this.params.indentSize || 20); // Allow custom indent size
    }
  }
};
</script>
